import React, { useEffect, useRef, useState } from 'react'
import './momentum-reveal-modal.styles.scss'
import { doc, getDoc } from 'firebase/firestore'
import { db, UseCloudGameTransactions } from '@/utils/firebase.utils'
import ImageContainer from '@/components/SHARED/image-container/image-container.component'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import { getUniqueDocumentFromFirestore, getCollectionFromFirestore, getQueryFromRtDb } from '@/utils/getDataFromDbSimplified'
import { sortPlayers } from '@/utils/sortPlayers'
import { faCropSimple } from '@fortawesome/free-solid-svg-icons'

// Stage flow: null → 'unrevealed' → 'revealing' (700ms) → 'revealed' → null

const MomentumRevealModal = ({ gameData, playerData, gameElements, playerList, pendingId, onDismiss }) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const location = useLocation();
  const [stage, setStage] = useState(null);
  const [activity, setActivity] = useState(null);
  const [element, setElement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTransferPlayer, setSelectedTransferPlayer] = useState(null);
  const [fetchingPlayers, setFetchingPlayers] = useState(false);
  const [showPlayerSelectModal, setShowPlayerSelectModal] = useState(false);

  const selectPlayerBtnRef = useRef(null);
  const modalHeadingRef = useRef(null);
  const modalRef = useRef(null);

  // useEffect(() => {
  //   if (stage === 'revealed' && element?.opt_removeTransfer) {
  //     setFetchingPlayers(true);
  //     getQueryFromRtDb({
  //       'path': `games/${gameData.gameId}/playerDocs`,
  //       'callback': callback,
  //       'once': true,
  //     })
  //     function callback(data) {
  //       console.log(data);
  //       const others = Object.values(data)
  //         .filter(p => p.playerId !== playerData.playerId && !p.playerId?.includes('_team'))
  //       setPlayerList(sortPlayers({
  //         playerList: others,
  //         filter: 'players',
  //         sortBy: 'name',
  //       }));
  //       setFetchingPlayers(false);
  //     }
  //   }
  // }, [stage, element]);

  // Move focus into the player-select sub-modal when it opens
  useEffect(() => {
    if (showPlayerSelectModal && modalHeadingRef.current) {
      modalHeadingRef.current.focus();
    }
  }, [showPlayerSelectModal]);

  useEffect(() => {
    console.log(pendingId);
    if (!pendingId) {
      setStage(null);
      setActivity(null);
      setElement(null);
      return;
    }
    getActivity(pendingId);
  }, [pendingId]);

  function getActivity(activityId) {
    getUniqueDocumentFromFirestore({
      'path': `games/${gameData.gameId}/activityRecords/${activityId}`,
      'once': true,
      'callback': (data) => {
        if (data) {
          console.log(data);
          setActivity(data);
          fetchElementFromDb(data.elementId);
        }
      }
    });
  }

  async function fetchElementFromDb(elementId) {
    console.log(elementId);
    getUniqueDocumentFromFirestore({
      'path': `games/${gameData.gameId}/gameElements/${elementId}`,
      'once': true,
      'callback': callback
    })
    function callback(data) {
      console.log(data);
      setElement(data);
      setStage('unrevealed');
    }
  }

  function handleReveal() {
    setStage('revealing');
    setTimeout(() => setStage('revealed'), 700);
  }

  async function handleDismiss() {
    setLoading(true);
    if (!location.pathname.includes('/preview/player/')) {
      await UseCloudGameTransactions('markRewardAsViewed', {
        gameId: gameData.gameId,
        playerId: playerData.playerId,
        rewardIdArr: [activity.id],
      });
    }
    if (selectedTransferPlayer) {
      console.log(selectedTransferPlayer);
      let randomlySelectedPlayerId = null;
      if (selectedTransferPlayer.random) {
        const randomPlayer = playerList[Math.floor(Math.random() * playerList.length)];
        randomlySelectedPlayerId = randomPlayer?.playerId;
      }
      const res = await UseCloudGameTransactions(
        'distributeMomentumTransfer',
        {
          gameId: gameData.gameId,
          playerId: randomlySelectedPlayerId
            ? randomlySelectedPlayerId
            : selectedTransferPlayer.playerId,
          elementId: element.id,
          momentumSource: playerData.displayName
        }
      )
      console.log(res);

    }
    setLoading(false);
    setStage(null);
    setActivity(null);
    setElement(null);
    setSelectedTransferPlayer(null);
    setShowPlayerSelectModal(false);
    onDismiss?.();
  }

  function handlePickRandom() {
    setSelectedTransferPlayer({ random: true });
  }

  function handleOpenPlayerModal() {
    setShowPlayerSelectModal(true);
  }

  function handleSelectPlayer(player) {
    setSelectedTransferPlayer(player);
    setShowPlayerSelectModal(false);
    // Return focus to the trigger button after modal closes
    setTimeout(() => selectPlayerBtnRef.current?.focus(), 0);
  }

  function handleClosePlayerModal() {
    setShowPlayerSelectModal(false);
    setTimeout(() => selectPlayerBtnRef.current?.focus(), 0);
  }

  // Trap focus inside sub-modal; close on Escape
  function handleModalKeyDown(e) {
    if (e.key === 'Escape') {
      handleClosePlayerModal();
      return;
    }
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = Array.from(modalRef.current.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )).filter(el => !el.disabled);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  if (!stage || !activity) return null;

  const direction = element?.opt_momentumDirection;

  function renderEffects() {
    if (!element) return null;

    if (direction === 'neutral') {
      return (
        <p className='neutral-message'>
          No items gained or lost — this card passed through without effect.
        </p>
      );
    }

    if (direction === 'positive') {
      const rows = [];
      if (element.opt_earnedPoints) {
        rows.push(
          <div key='pts' className='effect-row'>
            <span className='effect-label'>XP <ImageContainer className="tiny-icon" src="/images/icons/xp.png" /></span>
            <span className='effect-value'>+{element.opt_earnedPoints}</span>
          </div>
        );
      }
      if (element.opt_earnedCurrency) {
        rows.push(
          <div key='cur' className='effect-row'>
            <span className='effect-label'>Coins <ImageContainer className="tiny-icon" src="/images/icons/coin.png" /></span>
            <span className='effect-value'>+{element.opt_earnedCurrency}</span>
          </div>
        );
      }
      element.opt_earnedBadges?.forEach((badgeId, i) => {
        const badge = gameElements?.[badgeId];
        rows.push(
          <div key={`badge-${i}`} className='effect-row'>
            <span className='effect-label'>Badge</span>
            <span className='effect-value'>{badge?.name ?? 'Badge'}</span>
          </div>
        );
      });
      element.opt_earnedItems
        &&
        Object.values(element.opt_earnedItems).forEach((item, i) => {
          const itemEl = gameElements?.[item.id];
          rows.push(
            <div key={`item-${i}`} className='effect-row'>
              {/* <span className='effect-label'>Item</span>
            <span className='effect-value'>x{item.quantity ?? 1} {itemEl?.name ?? 'Item'}</span> */}
              <span className='effect-label'>Item  <ImageContainer className="tiny-icon" src="/images/icons/items.png" /></span>
              <span className='effect-value'>
                <span className='name'>{itemEl?.name ?? 'Item'}</span>
                <span>+1</span>
                <ImageContainer className="tiny-icon" src={itemEl?.icon ?? '/images/icons/item.png'} />
                {/* {differs && <span className='original-amount'> (card: -{cardQty})</span>} */}
              </span>
            </div>
          );
        });
      if (element.opt_earnedPrizePacks && Object.values(element.opt_earnedPrizePacks).length > 0) {
        Object.values(element.opt_earnedPrizePacks).forEach((ppId, i) => {
          const pp = gameElements?.[ppId];
          rows.push(
            <div key={`pp-${i}`} className='effect-row'>
              <span className='effect-label'>Prize Pack</span>
              <span className='effect-value'>
                <span className='name'>{pp?.name ?? 'Prize Pack'}</span>
              </span>
            </div>
          );
        });
      }
      if (element.opt_earnedMomentum?.length) {
        rows.push(
          <div key='mom' className='effect-row'>
            <span className='effect-label'>Momentum</span>
            <span className='effect-value'>
              +{element.opt_earnedMomentum.length} more card{element.opt_earnedMomentum.length > 1 ? 's' : ''}
            </span>
          </div>
        );
      }
      return rows.length > 0
        ? <div className='effects-list'>{rows}</div>
        : <p className='neutral-message'>Congratulations!</p>;
    }

    if (direction === 'negative') {
      const rows = [];

      const dataEntries = Object.values(activity?.data || {});
      const actualCurrencyEntry = dataEntries.find(d => d.type === 'currencyRemoved');
      const actualItemsMap = dataEntries
        .filter(d => d.type === 'itemRemoved')
        .reduce((acc, d) => { acc[d.id] = d.quantity; return acc; }, {});

      if (element.opt_removeCurrency > 0) {
        const cardAmt = element.opt_removeCurrency;
        const actualAmt = actualCurrencyEntry?.quantity ?? 0;
        const differs = actualAmt !== cardAmt;
        rows.push(
          <div key='cur' className='effect-row negative-effect'>
            <span className='effect-label'>Coins <ImageContainer className="tiny-icon" src="/images/icons/coin.png" /></span>
            <span className='effect-value'>
              -{actualAmt}
              {differs && <span className='original-amount'> (card: -{cardAmt})</span>}
            </span>
          </div>
        );
      }
      if (element.opt_removeItems) {
        Object.values(element.opt_removeItems).forEach(item => {
          const element = gameElements?.[item.id];
          const cardQty = item.quantity ?? 1;
          const actualQty = actualItemsMap[item.id] ?? 0;
          const differs = actualQty !== cardQty;
          rows.push(
            <div key={item.id} className='effect-row negative-effect'>
              <span className='effect-label'>Item  <ImageContainer className="tiny-icon" src="/images/icons/items.png" /></span>
              <span className='effect-value'>
                -{cardQty}
                <span className='name'>{element?.name ?? 'Item'}</span>
                <ImageContainer className="tiny-icon" src={element?.icon ?? '/images/icons/item.png'} />
                {/* {differs && <span className='original-amount'> (card: -{cardQty})</span>} */}
              </span>
            </div>
          );
        });
      }
      return rows.length > 0
        ? <div className='effects-list'>{rows}</div>
        : <p className='neutral-message'>Fortunately, nothing could be taken from you.</p>;
    }

    return null;
  }

  const directionLabel = direction === 'positive' ? '▲ Nice!'
    : direction === 'negative' ? '▼ Oof!'
      : '● Meh';

  const selectedPlayerName = selectedTransferPlayer?.random
    ? 'Random Player'
    : selectedTransferPlayer
      ? `${selectedTransferPlayer.displayName}`
      : null;

  return (
    <>
      <div className={`momentum-reveal-overlay ${stage} ${direction ?? ''} ${screenSize}`}>
        <div className={`momentum-reveal-card ${stage} ${direction ?? ''}`}>

          {(stage === 'unrevealed' || stage === 'revealing') && (
            <div className={`card-phase unrevealed-phase${stage === 'revealing' ? ' fading' : ''}`}>
              <div className='mystery-card-icon'>
                <span className='mystery-q'>?</span>
              </div>
              <h2>You Drew a Momentum Card!</h2>
              <p className='meta'>Something is about to happen...</p>
              <div className='g-space-1'></div>
              <button
                className={`g-button primary${screenSize === 'mobile' ? ' med-btn' : ''}`}
                onClick={handleReveal}
                disabled={stage === 'revealing'}
              >
                Reveal Card
              </button>
            </div>
          )}

          {stage === 'revealed' && (
            <div className={`card-phase revealed-phase ${direction ?? ''}`}>
              <div className={`direction-banner ${direction ?? ''}`}>
                {directionLabel}
              </div>
              <div className='g-space-0-5'></div>
              <ImageContainer
                src={element?.icon ?? activity.icon}
                alt={element?.name ?? 'Momentum Card'}
                className='revealed-icon'
              />
              <h2>{element?.name ?? activity.title ?? 'Momentum Card'}</h2>
              {element?.desc && (
                <p
                  className='momentum-desc meta'
                  dangerouslySetInnerHTML={{ __html: element.desc }}
                />
              )}
              {direction && (
                <>
                  <hr />
                  <div className='effect-details'>{renderEffects()}</div>
                </>
              )}
              <div className='g-space-1'></div>

              {element?.opt_removeTransfer && (
                <div className='transfer-content'>
                  <div className='meta transfer-note' style={{ marginBottom: '0.5rem' }}>
                    Choose where your removed items or currency go.
                  </div>
                  <div className='transfer-buttons'>
                    <button
                      className={`g-button med-btn${!selectedTransferPlayer ? ' transfer-btn-prompt' : ''}`}
                      onClick={handlePickRandom}
                      disabled={fetchingPlayers}
                      style={{ fontSize: '0.85rem' }}
                    >
                      Pick a Random Player
                    </button>
                    <button
                      className={`g-button med-btn${!selectedTransferPlayer ? ' transfer-btn-prompt' : ''}`}
                      ref={selectPlayerBtnRef}
                      onClick={handleOpenPlayerModal}
                      disabled={fetchingPlayers}
                      style={{ fontSize: '0.85rem' }}
                    >
                      Choose a Player
                    </button>
                  </div>
                </div>
              )}

              {/* aria-live region: announced by screen readers when selection changes */}
              {element?.opt_removeTransfer && (
                <div role='status' aria-live='polite' aria-atomic='true'>
                  {selectedTransferPlayer && (
                    <div className='selected-transfer-player'>
                      <div>
                        <span style={{ fontWeight: 600 }}>Sending to: </span>
                        <span style={{ color: '#1d4ed8', fontWeight: 700 }}>{selectedPlayerName}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className='buttons'>
                {loading ? (
                  <button className='g-button'><ThreeDotsLoader /></button>
                ) : (
                  <button
                    className={`g-button primary${screenSize === 'mobile' ? ' med-btn' : ''}
                    ${!selectedTransferPlayer && element?.opt_removeTransfer ? ' disabled' : ''}`}
                    onClick={handleDismiss}
                  >
                    Got It!
                  </button>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Player-select sub-modal — rendered outside the main card so z-index stacking is clean */}
      {showPlayerSelectModal && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='transfer-modal-title'
          onKeyDown={handleModalKeyDown}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)', padding: '1rem' }}
        >
          <div
            ref={modalRef}
            style={{ background: '#fff', borderRadius: '1rem', padding: '1.5rem', width: '100%', maxWidth: '360px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: '0.75rem', boxShadow: '0 10px 40px rgba(0,0,0,0.45)' }}
          >
            <h3
              id='transfer-modal-title'
              ref={modalHeadingRef}
              tabIndex={-1}
              style={{ margin: 0, fontSize: '1.1rem', textAlign: 'center', outline: 'none' }}
            >
              Select a Player
            </h3>
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {playerList.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '0.9rem', margin: 0 }}>
                  No other players found.
                </p>
              ) : (
                playerList.map(p => (
                  <button
                    key={p.playerId}
                    className={`g-button${selectedTransferPlayer?.playerId === p.playerId ? ' primary' : ''}`}
                    onClick={() => handleSelectPlayer(p)}
                    style={{ fontSize: '0.9rem' }}
                  >
                    {p.displayName}
                  </button>
                ))
              )}
            </div>
            <button
              className='g-button'
              onClick={handleClosePlayerModal}
              style={{ fontSize: '0.9rem' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default MomentumRevealModal
